<script setup>
import {ref} from "vue";
import {useFetch} from "@/plugins/fetch";
import {useToast} from "primevue/usetoast";

import Password from 'primevue/password';

const fetch = useFetch();
const toast = useToast();

const form = ref({
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: '',
});

const isLoading = ref(false);

const doPasswordsMatch = () => {
  return form.value.newPassword === form.value.confirmNewPassword;
}

const submitForm = async () => {
  if (!form.value.currentPassword || !form.value.newPassword) {
    toast.add({
      severity: 'warn',
      summary: 'Missing Fields',
      detail: 'Please fill out all password fields.',
      life: 3000
    });
    return;
  }
  if (!doPasswordsMatch()) {
    toast.add({severity: 'error', summary: 'Mismatch', detail: 'New passwords do not match.', life: 3000});
    return;
  }

  isLoading.value = true;
  try {
    await fetch('user/security/change-password/', {
      method: 'POST',
      body: {
        currentPassword: form.value.currentPassword,
        newPassword: form.value.newPassword
      }
    });
    toast.add({severity: 'success', summary: 'Success', detail: 'Password changed successfully', life: 3000});
    resetForm();
  } catch (error) {
    toast.add({severity: 'error', summary: 'Error', detail: error.message || 'Failed to change password', life: 3000});
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  form.value = {currentPassword: '', newPassword: '', confirmNewPassword: ''};
};
</script>

<template>
  <div class="password-card">
    <div class="card-header">
      <div class="header-icon">
        <i class="fas fa-lock"></i>
      </div>
      <div class="header-text">
        <h3>Change Password</h3>
        <p>Update your account password</p>
      </div>
    </div>

    <div class="card-body">
      <form @submit.prevent="submitForm">
        <div class="form-group">
          <label for="currentPassword">
            <i class="fas fa-key"></i>
            Current Password
          </label>
          <Password 
            id="currentPassword" 
            v-model="form.currentPassword" 
            :feedback="false" 
            toggleMask
            class="password-input"
            placeholder="Enter current password"
          />
        </div>

        <div class="form-divider">
          <span>New Password</span>
        </div>

        <div class="form-group">
          <label for="newPassword">
            <i class="fas fa-lock"></i>
            New Password
          </label>
          <Password 
            id="newPassword" 
            v-model="form.newPassword" 
            toggleMask
            class="password-input"
            placeholder="Enter new password"
          >
            <template #header><span class="password-hint-header">Choose a strong password</span></template>
            <template #footer>
              <div class="password-hints">
                <p>Password should include:</p>
                <ul>
                  <li><i class="fas fa-check-circle"></i> At least one lowercase letter</li>
                  <li><i class="fas fa-check-circle"></i> At least one uppercase letter</li>
                  <li><i class="fas fa-check-circle"></i> At least one number</li>
                  <li><i class="fas fa-check-circle"></i> Minimum 8 characters</li>
                </ul>
              </div>
            </template>
          </Password>
        </div>

        <div class="form-group">
          <label for="confirmNewPassword">
            <i class="fas fa-lock"></i>
            Confirm New Password
          </label>
          <Password 
            id="confirmNewPassword" 
            v-model="form.confirmNewPassword" 
            :feedback="false" 
            toggleMask
            class="password-input"
            :class="{'password-error': form.confirmNewPassword && !doPasswordsMatch()}"
            placeholder="Confirm new password"
          />
          <span v-if="form.confirmNewPassword && !doPasswordsMatch()" class="error-text">
            <i class="fas fa-exclamation-circle"></i>
            Passwords do not match
          </span>
        </div>

        <div class="form-actions">
          <button type="button" class="btn-cancel" @click="resetForm" :disabled="isLoading">
            <i class="fas fa-times"></i>
            Cancel
          </button>
          <button type="submit" class="btn-primary" :disabled="isLoading">
            <i v-if="!isLoading" class="fas fa-check"></i>
            <i v-else class="fas fa-circle-notch fa-spin"></i>
            {{ isLoading ? 'Updating...' : 'Update Password' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.password-card {
  background: var(--calid-card-bg, rgba(17, 24, 39, 0.8));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 16px;
  overflow: hidden;
  height: 100%;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-bottom: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  background: var(--calid-card-bg, rgba(17, 24, 39, 0.8));
}

.header-icon {
  width: 44px;
  height: 44px;
  background: rgba(100, 50, 200, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon i {
  font-size: 1.125rem;
  color: white;
}

.header-text h3 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0 0 0.125rem;
}

.header-text p {
  font-size: 0.85rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
}

.card-body {
  padding: 1.25rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--calid-text-secondary, #94a3b8);
  margin-bottom: 0.5rem;
}

.form-group label i {
  font-size: 0.75rem;
  color: #00d4ff;
}

:deep(.password-input) {
  width: 100%;
}

:deep(.password-input .p-password-input) {
  width: 100%;
  padding: 0.75rem 1rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 10px;
  color: var(--calid-text-primary, #f0f0f5);
  font-size: 0.95rem;
}

:deep(.password-input .p-password-input:focus) {
  border-color: #00d4ff;
  box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.15);
}

:deep(.password-input .p-password-input::placeholder) {
  color: var(--calid-text-tertiary, #64748b);
}

:deep(.password-error .p-password-input) {
  border-color: #ef4444;
}

:deep(.password-error .p-password-input:focus) {
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.15);
}

:deep(.p-password-toggle-icon) {
  color: var(--calid-text-secondary, #94a3b8);
}

.form-divider {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1.5rem 0;
}

.form-divider::before,
.form-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--calid-border, rgba(255, 255, 255, 0.08));
}

.form-divider span {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--calid-text-tertiary, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.error-text {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8rem;
  color: #f87171;
  margin-top: 0.5rem;
}

.password-hint-header {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
}

.password-hints {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
}

.password-hints p {
  font-size: 0.8rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0 0 0.5rem;
}

.password-hints ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.password-hints li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--calid-text-secondary, #94a3b8);
  padding: 0.25rem 0;
}

.password-hints li i {
  color: #10b981;
  font-size: 0.7rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
}

.btn-cancel,
.btn-primary {
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-cancel {
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  color: var(--calid-text-secondary, #94a3b8);
}

.btn-cancel:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}

.btn-primary {
  background: #10b981;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #059669;
}

.btn-cancel:disabled,
.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Light mode */
:root:not(.dark-version) .password-card {
  background: white;
  border-color: #e5e7eb;
}

:root:not(.dark-version) .card-header {
  border-bottom-color: #f3f4f6;
}

:root:not(.dark-version) .header-text h3 {
  color: #1f2937;
}

:root:not(.dark-version) .header-text p {
  color: #6b7280;
}

:root:not(.dark-version) .form-group label {
  color: #374151;
}

:root:not(.dark-version) :deep(.password-input .p-password-input) {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #1f2937;
}

:root:not(.dark-version) .form-divider::before,
:root:not(.dark-version) .form-divider::after {
  background: #e5e7eb;
}

:root:not(.dark-version) .form-divider span {
  color: #9ca3af;
}

:root:not(.dark-version) .btn-cancel {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #374151;
}

:root:not(.dark-version) .btn-cancel:hover:not(:disabled) {
  background: #e5e7eb;
}

:root:not(.dark-version) .form-actions {
  border-top-color: #f3f4f6;
}
</style>
